import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useParentAuth } from '../context/ParentAuthContext';
import { tokenManager } from '../lib/security';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Loader2, LogIn, UserPlus, Eye, EyeOff, Check,
  Mail, Clock, ShieldAlert, Trophy, Target, BarChart3, Users, ArrowLeft, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { isNativeApp, WEB_URL } from '../lib/platform';
import MiCoachLogo from '../components/MiCoachLogo';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register, logout } = useAuth();
  const { loginParent } = useParentAuth();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan') || 'free';
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', selected_plan: selectedPlan });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const u = await login(formData.email, formData.password);
        if (u?.role === 'parent') {
          // Login role-aware: un familiar que entra por el acceso de entrenador
          // se enruta directo a su portal reutilizando el token ya emitido,
          // sin obligarle a volver a iniciar sesión en /parent/acceso.
          const t = tokenManager.getToken();
          await logout();                              // limpia el contexto de coach
          loginParent({ access_token: t, user: u });   // sesión en el contexto de familias
          toast.success(`Bienvenido/a${u.name ? ', ' + u.name.split(' ')[0] : ''}`);
          navigate('/parent/mensajes', { replace: true });
          return;
        }
        toast.success('Bienvenido de vuelta');
        navigate('/dashboard');
      } else {
        // Register with selected plan → requiere verificar el email por código.
        await register(formData.email, formData.password, formData.name, selectedPlan);
        toast.success('¡Cuenta creada! Verifica tu email.');
        navigate(`/verificar-codigo?email=${encodeURIComponent(formData.email)}&plan=${selectedPlan}`);
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      // Email sin verificar: el backend devuelve un detail estructurado.
      if (status === 403 && detail && typeof detail === 'object' && detail.code === 'EMAIL_NOT_VERIFIED') {
        toast.info('Verifica tu email para continuar. Te hemos enviado un nuevo código.');
        navigate(`/verificar-codigo?email=${encodeURIComponent(detail.email || formData.email)}`);
        return;
      }
      const message = (typeof detail === 'string' ? detail : detail?.message) || 'Error en la autenticación';
      if (status === 403) {
        toast.error(message, { duration: 6000 });
      } else if (status === 429) {
        toast.error('Demasiados intentos. Espera un momento.');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Target, text: 'Planificación de microciclos' },
    { icon: Users, text: 'Gestión completa de plantilla' },
    { icon: Trophy, text: 'Análisis de partidos y rendimiento' },
    { icon: BarChart3, text: 'Wellness y seguimiento de atletas' },
  ];

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Introduce tu email');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
      toast.success('Email enviado correctamente');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al enviar el email';
      // Even on error, show success to prevent email enumeration
      if (error.response?.status === 404) {
        setForgotSuccess(true);
        toast.success('Si el email existe, recibirás instrucciones');
      } else {
        toast.error(message);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-0)' }}>
        <div
          className="w-full max-w-md p-8 rounded-2xl text-center"
          style={{ background: 'var(--surface-0)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-lg)' }}
        >
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{ background: 'rgba(var(--primary-rgb),0.08)', border: '1px solid rgba(var(--primary-rgb),0.18)' }}>
            <Check className="w-8 h-8" style={{ color: 'var(--primary)' }} />
          </div>
          <h2 className="text-2xl font-black mb-2" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--text-0)' }}>
            ¡CUENTA CREADA!
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-2)' }}>
            Tu cuenta está lista. Ya puedes iniciar sesión con tu email y contraseña.
          </p>
          <button
            onClick={() => { setRegistrationSuccess(false); setIsLogin(true); }}
            className="w-full py-2.5 rounded-xl font-bold text-sm btn-glow"
            style={{ background: 'var(--primary)', color: 'var(--primary-ink)', transition: 'background-color 200ms' }}
          >
            Ir a iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  // Forgot password view
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#101820' }}>
        <div className="w-full max-w-[400px]">
          {/* Back button */}
          <button
            onClick={() => { setShowForgotPassword(false); setForgotSuccess(false); setForgotEmail(''); }}
            className="flex items-center gap-2 mb-6 text-sm font-medium"
            style={{ color: '#7a8490' }}
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Volver al login
          </button>

          <div
            className="p-8 rounded-2xl"
            style={{ background: '#141E28', border: '1px solid #1A242D' }}
          >
            {forgotSuccess ? (
              // Success state
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: 'rgba(var(--primary-rgb),0.08)', border: '1px solid rgba(var(--primary-rgb),0.18)' }}>
                  <Mail className="w-8 h-8" style={{ color: 'var(--primary)' }} />
                </div>
                <h2 className="text-2xl font-black mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#f0f2f5' }}>
                  ¡EMAIL ENVIADO!
                </h2>
                <p className="text-sm mb-6" style={{ color: '#7a8490' }}>
                  Si existe una cuenta con <span style={{ color: 'var(--primary)' }}>{forgotEmail}</span>, recibirás un email con instrucciones para restablecer tu contraseña.
                </p>
                <p className="text-xs mb-6" style={{ color: '#5a6570' }}>
                  Revisa también tu carpeta de spam.
                </p>
                <button
                  onClick={() => { setShowForgotPassword(false); setForgotSuccess(false); setForgotEmail(''); }}
                  className="w-full py-2.5 rounded-xl font-bold text-sm btn-glow"
                  style={{ background: 'var(--primary)', color: '#000', transition: 'background-color 200ms' }}
                  data-testid="back-to-login-success-btn"
                >
                  Volver al login
                </button>
              </div>
            ) : (
              // Form state
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'rgba(77,214,255,0.08)', border: '1px solid rgba(77,214,255,0.18)' }}>
                    <KeyRound className="w-7 h-7" style={{ color: '#4dd6ff' }} />
                  </div>
                  <h2 className="text-2xl font-black mb-1" style={{ fontFamily: 'Inter, sans-serif', color: '#f0f2f5' }}>
                    RECUPERAR CONTRASEÑA
                  </h2>
                  <p className="text-sm" style={{ color: '#7a8490' }}>
                    Introduce tu email y te enviaremos instrucciones para restablecer tu contraseña.
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold" style={{ color: '#7a8490' }}>Email de tu cuenta</label>
                    <input
                      type="email"
                      placeholder="entrenador@club.com"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl text-sm auth-input"
                      style={{ background: '#0c1218', border: '1px solid #243040', color: '#f0f2f5', outline: 'none' }}
                      onFocus={e => e.target.style.borderColor = 'rgba(77,214,255,0.40)'}
                      onBlur={e => e.target.style.borderColor = '#243040'}
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                    style={{
                      background: forgotLoading ? 'rgba(77,214,255,0.6)' : '#4dd6ff',
                      color: '#000',
                      transition: 'background-color 200ms'
                    }}
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                    ) : (
                      <><Mail className="w-4 h-4" /> Enviar instrucciones</>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: '#101820' }}>
      {/* Left: Branding */}
      <div className="hidden lg:flex flex-col justify-between w-[42%] p-12 relative overflow-hidden"
        style={{
          background: '#0c1218',
          borderRight: '1px solid #1A242D'
        }}
      >
        {/* Subtle glow */}
        <div className="absolute top-[-80px] left-[-80px] w-[350px] h-[350px] rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(var(--primary-rgb),0.30) 0%, transparent 70%)' }} />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <MiCoachLogo size={40} variant="on-emerald" />
            <span className="font-black text-xl tracking-wide" style={{ fontFamily: 'Inter, sans-serif', color: '#f0f2f5' }}>MiCoach</span>
          </div>

          <div>
            <p className="text-[10px] font-black mb-3 flex items-center gap-2" style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.15em' }}>
              <span className="w-5 h-px" style={{ background: 'var(--primary)' }} />
              PLATAFORMA DE COACHING
            </p>
            <h1 className="text-5xl font-black leading-[0.92] mb-5" style={{ fontFamily: 'Inter, sans-serif', color: '#f0f2f5' }}>
              EL SISTEMA
              <br />
              <span style={{ color: 'var(--primary)' }}>DEL ENTRENADOR</span>
              <br />
              MODERNO.
            </h1>
            <p className="text-sm leading-relaxed mb-6" style={{ color: '#7a8490' }}>
              Planifica con estructura. Decide con datos. Crece como profesional.
            </p>

            {/* Active users indicator */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl mb-6" style={{ background: '#141E28', border: '1px solid #1A242D' }}>
              <div className="flex -space-x-2">
                {['var(--primary)','#4dd6ff','#ffd34d','#a78bfa','#ff4d6d'].map((c,i)=>(
                  <div key={i} className="w-7 h-7 rounded-full flex items-center justify-center text-[8px] font-black"
                    style={{ background: c, color: '#000', border: '2px solid #101820', zIndex: 5-i }}>{String.fromCharCode(65+i)}</div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--primary)' }} />
                  <span className="text-xs font-bold" style={{ color: '#f0f2f5' }}>247 entrenadores activos</span>
                </div>
                <p className="text-[10px]" style={{ color: '#5a6570' }}>en 48 clubes de España</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-2">
          {features.map((f, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: '#141E28', border: '1px solid #1A242D' }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(var(--primary-rgb),0.10)', border: '1px solid rgba(var(--primary-rgb),0.20)' }}>
                <f.icon className="w-3.5 h-3.5" style={{ color: 'var(--primary)' }} />
              </div>
              <span className="text-xs" style={{ color: '#ADB5BD' }}>{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12" style={{ background: '#101820' }}>
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <MiCoachLogo size={36} variant="on-emerald" />
          <span className="font-black text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#f0f2f5' }}>MiCoach</span>
        </div>

        <div className="w-full max-w-[380px]">
          <div className="mb-8">
            <h2 className="text-3xl font-black mb-1.5" style={{ fontFamily: 'Inter, sans-serif', color: '#f0f2f5' }}>
              {isLogin ? 'INICIAR SESION' : 'CREAR CUENTA'}
            </h2>
            <p className="text-sm" style={{ color: '#7a8490' }}>
              {isLogin ? 'Accede a tu plataforma de coaching' : 'Crea tu cuenta gratis y empieza ahora'}
            </p>
          </div>

          {/* Toggle tabs — el registro no se ofrece dentro de la app nativa
              (se hace en la web); evita CTAs que Apple/Google bloquean. */}
          {!isNativeApp() && (
          <div
            className="flex gap-1 p-1 rounded-xl mb-6"
            style={{ background: '#141E28', border: '1px solid #1A242D' }}
          >
            {[{ key: true, label: 'Iniciar sesión', icon: LogIn }, { key: false, label: 'Registrarse', icon: UserPlus }].map(t => (
              <button
                key={String(t.key)}
                onClick={() => setIsLogin(t.key)}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold"
                style={{
                  background: isLogin === t.key ? '#1A242D' : 'transparent',
                  color: isLogin === t.key ? '#f0f2f5' : '#7a8490',
                  border: isLogin === t.key ? '1px solid #243040' : '1px solid transparent',
                  transition: 'background-color 200ms, color 200ms'
                }}
              >
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold" style={{ color: '#7a8490' }}>Nombre completo</label>
                <input type="text" placeholder="Tu nombre" value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl text-sm auth-input"
                  style={{ background: '#141E28', border: '1px solid #1A242D', color: '#f0f2f5', outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = 'rgba(var(--primary-rgb),0.40)'}
                  onBlur={e => e.target.style.borderColor = '#1A242D'}
                  required minLength={2} data-testid="name-input" />
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-semibold" style={{ color: '#7a8490' }}>Email</label>
              <input type="email" placeholder="entrenador@club.com" value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-xl text-sm auth-input"
                style={{ background: '#141E28', border: '1px solid #1A242D', color: '#f0f2f5', outline: 'none' }}
                onFocus={e => e.target.style.borderColor = 'rgba(var(--primary-rgb),0.40)'}
                onBlur={e => e.target.style.borderColor = '#1A242D'}
                required data-testid="email-input" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold" style={{ color: '#7a8490' }}>Contraseña</label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs font-medium hover:underline"
                    style={{ color: '#4dd6ff' }}
                    data-testid="forgot-password-link"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'}
                  placeholder={isLogin ? 'Tu contraseña' : 'Mínimo 6 caracteres'}
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3.5 py-2.5 pr-10 rounded-xl text-sm auth-input"
                  style={{ background: '#141E28', border: '1px solid #1A242D', color: '#f0f2f5', outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = 'rgba(var(--primary-rgb),0.40)'}
                  onBlur={e => e.target.style.borderColor = '#1A242D'}
                  required minLength={6} data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#7a8490' }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-bold text-sm mt-2 btn-glow flex items-center justify-center gap-2"
              style={{
                background: loading ? 'rgba(var(--primary-rgb),0.6)' : 'var(--primary)',
                color: 'var(--primary-ink)',
                transition: 'background-color 200ms'
              }}
              data-testid="submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</>
              ) : isLogin ? (
                <><LogIn className="w-4 h-4" /> Entrar</>
              ) : (
                <><UserPlus className="w-4 h-4" /> Crear cuenta</>
              )}
            </button>
          </form>

          {isNativeApp() && (
            <p className="text-center text-xs mt-6" style={{ color: '#7a8490' }}>
              ¿Aún no tienes cuenta? Créala en <span style={{ color: '#f0f2f5', fontWeight: 600 }}>{WEB_URL}</span> y luego inicia sesión aquí.
            </p>
          )}

          {/* Enlaces legales: obligatorio que sean accesibles sin sesión
              (art. 10 LSSI-CE) y antes de crear la cuenta. */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <a href="/aviso-legal" className="text-xs" style={{ color: '#7a8490' }}>Aviso legal</a>
            <a href="/privacidad" className="text-xs" style={{ color: '#7a8490' }}>Privacidad</a>
            <a href="/cookies" className="text-xs" style={{ color: '#7a8490' }}>Cookies</a>
            <a href="/terminos" className="text-xs" style={{ color: '#7a8490' }}>Términos</a>
          </div>

          <p className="text-center text-xs mt-4" style={{ color: '#4a5568' }}>
            © 2026 MiCoach • Plataforma para entrenadores
          </p>
        </div>
      </div>
    </div>
  );
}
